import dayjs, {Dayjs} from 'dayjs';
import {isString} from 'lodash';

import {CreateReviewFormPaylaodType, EditReviewFormPaylaodType} from '../../../../service/reviews/custom-reviews';
import {ReviewFormSourcesType} from '../../../../service/reviews/reviews-form-sources';
import {ReviewDataType} from '../../../../service/reviews/reviews-type';
import {ReviewEntityScopeEnum} from '../review-brand-company-scope-card/review-brand-company-scope-card.types';

import {
    ReviewAttachmentType,
    ReviewAttachmentTypeEnum,
    ReviewFormFieldNamesEnum,
    ReviewFormFieldsType,
} from './review-form-type';

export function extractSource(
    sourceId: ReviewFormSourcesType['id'],
    sources: Array<ReviewFormSourcesType> | null
): ReviewFormSourcesType | null {
    if (!sources) {
        return null;
    }

    const source = sources.find(({id}) => id === sourceId);

    if (!source) {
        return null;
    }

    return source;
}

export function getDisabledDate(current: Dayjs): boolean {
    return current.isAfter(dayjs());
}

function escapeHtmlAttribute(value: string): string {
    return value.replaceAll('&', '&amp;').replaceAll('"', '&quot;');
}

function buildReviewCommentWithAttachments(
    content: string | undefined,
    attachments: Array<ReviewAttachmentType> | undefined
): string | null {
    const text = (content || '').trim();
    const attachmentLines =
        attachments
            ?.map((attachment) => {
                const src = attachment.src.trim();

                if (!src) {
                    return null;
                }

                if (attachment.type === ReviewAttachmentTypeEnum.video) {
                    return `<video src="${escapeHtmlAttribute(src)}"></video>`;
                }

                return `<img src="${escapeHtmlAttribute(src)}" alt="" />`;
            })
            .filter(Boolean) || [];

    const media = attachmentLines.join('\n');
    const comment = [media, text].filter(Boolean).join('\n');

    return comment || null;
}

function getVideoTagSrc(videoTag: HTMLVideoElement): string | null {
    const src = videoTag.getAttribute('src');

    if (src && src.trim()) {
        return src;
    }

    const sourceTag = videoTag.querySelector('source');
    const sourceSrc = sourceTag?.getAttribute('src');

    return sourceSrc && sourceSrc.trim() ? sourceSrc : null;
}

function extractReviewCommentAttachments(comment: string | null): {
    text: string;
    attachments: Array<ReviewAttachmentType>;
} {
    if (!comment) {
        return {text: '', attachments: []};
    }

    const documentNode = new DOMParser().parseFromString(comment, 'text/html');
    const mediaNodes = [...documentNode.querySelectorAll('img, video')];

    const attachments = mediaNodes
        .map((node, index) => {
            if (node.tagName.toLowerCase() === 'img') {
                const srcMEdia = node.getAttribute('src');

                if (!srcMEdia || !srcMEdia.trim()) {
                    return null;
                }

                return {
                    id: `review-image-${index}`,
                    type: ReviewAttachmentTypeEnum.image,
                    src: srcMEdia,
                };
            }

            const src = getVideoTagSrc(node as HTMLVideoElement);

            if (!src) {
                return null;
            }

            return {
                id: `review-video-${index}`,
                type: ReviewAttachmentTypeEnum.video,
                src,
                previewSrc: node.getAttribute('poster') || undefined, // eslint-disable-line no-undefined
            };
        })
        .filter(Boolean);

    documentNode.querySelectorAll('img, video').forEach((node) => node.remove());

    const text = (documentNode.documentElement.textContent || '').replace(/(\n){3,}/g, '\n\n').trim();

    return {
        text,
        attachments,
    };
}

export function serializeCreateReviewForm(fields: ReviewFormFieldsType): CreateReviewFormPaylaodType {
    const companyId = fields[ReviewFormFieldNamesEnum.company];

    return {
        catalog_id: fields[ReviewFormFieldNamesEnum.source],
        brand_id: fields[ReviewFormFieldNamesEnum.brand],
        company_id: companyId ? Number(companyId) : null,
        creation_time: fields[ReviewFormFieldNamesEnum.createdAt].toISOString(),
        author: fields[ReviewFormFieldNamesEnum.author] || null,
        origin_url: fields[ReviewFormFieldNamesEnum.url] || null,
        rating: fields[ReviewFormFieldNamesEnum.rating] || null,
        comment: buildReviewCommentWithAttachments(
            fields[ReviewFormFieldNamesEnum.content],
            fields[ReviewFormFieldNamesEnum.attachments]
        ),
        tag_ids: (fields[ReviewFormFieldNamesEnum.tags] || []).map((tag) => tag.id) || null,
        user_fields: {
            email: fields[ReviewFormFieldNamesEnum.email] || null,
            phone: fields[ReviewFormFieldNamesEnum.phone] || null,
        },
        notes:
            fields[ReviewFormFieldNamesEnum.notes]?.map(({id, text}) => (isString(id) ? {text} : {text, id})) || null,
    };
}

export function serializeEditReviewForm(fields: ReviewFormFieldsType, id: number): EditReviewFormPaylaodType {
    const companyId = fields[ReviewFormFieldNamesEnum.company];

    return {
        id,
        catalog_id: fields[ReviewFormFieldNamesEnum.source],
        brand_id: fields[ReviewFormFieldNamesEnum.brand],
        company_id: companyId ? Number(companyId) : null,
        creation_time: fields[ReviewFormFieldNamesEnum.createdAt].toISOString(),
        author: fields[ReviewFormFieldNamesEnum.author] || null,
        origin_url: fields[ReviewFormFieldNamesEnum.url] || null,
        rating: fields[ReviewFormFieldNamesEnum.rating] || null,
        comment: buildReviewCommentWithAttachments(
            fields[ReviewFormFieldNamesEnum.content],
            fields[ReviewFormFieldNamesEnum.attachments]
        ),
        tag_ids: (fields[ReviewFormFieldNamesEnum.tags] || []).map((tag) => tag.id) || null,
        user_fields: {
            email: fields[ReviewFormFieldNamesEnum.email] || null,
            phone: fields[ReviewFormFieldNamesEnum.phone] || null,
        },
        notes:
            fields[ReviewFormFieldNamesEnum.notes]?.map(({id: noteId, text}) =>
                isString(noteId) ? {text} : {text, id: noteId}
            ) || null,
    };
}

/* eslint-disable no-undefined */
// eslint-disable-next-line complexity
export function deserializeReview(review: ReviewDataType): ReviewFormFieldsType {
    const [authorName] = (review.author || '+').split('+');
    const {text, attachments} = extractReviewCommentAttachments(review.comment);

    return {
        [ReviewFormFieldNamesEnum.source]: review.catalogId,
        [ReviewFormFieldNamesEnum.entityScope]: review.company
            ? ReviewEntityScopeEnum.company
            : ReviewEntityScopeEnum.brand,
        [ReviewFormFieldNamesEnum.brand]: review.brand.id,
        [ReviewFormFieldNamesEnum.company]: review.company ? String(review.company?.id) : undefined,
        [ReviewFormFieldNamesEnum.createdAt]: dayjs(review.createdInCatalog),
        [ReviewFormFieldNamesEnum.author]: authorName ? authorName.trim() : '',
        [ReviewFormFieldNamesEnum.phone]: review.userFields.phone || undefined,
        [ReviewFormFieldNamesEnum.url]: review.originUrl || undefined,
        [ReviewFormFieldNamesEnum.rating]: review.rating || undefined,
        [ReviewFormFieldNamesEnum.content]: text || undefined,
        [ReviewFormFieldNamesEnum.email]: review.userFields.email || undefined,
        [ReviewFormFieldNamesEnum.attachments]: attachments,
        [ReviewFormFieldNamesEnum.tags]: review.tags || [],
        [ReviewFormFieldNamesEnum.notes]: review.notes || [],
    };
}
/* eslint-enable no-undefined */
